import type { SimpleChanges } from '@angular/core';
import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { Router, RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';
import type { Subject } from 'rxjs';
import { combineLatest, takeUntil } from 'rxjs';

import type { UserIdMap } from '@core/account-data';
import { UserSelectors } from '@core/account-data';
import { AnalyticsViewContext, provideAnalyticsViewContext } from '@core/analytics-legacy';
import {
  collapse,
  filterBoolean,
  NgTemplateAssertType,
  trackByIndex,
  trackByProp,
} from '@core/common-legacy';
import type { JobsIdMap } from '@core/offers-data';
import { JobsCombinedSelectors } from '@core/offers-data';
import { OffersPageGroupHeadingComponent } from '@core/offers-ui';
import { RequisitionsListParam } from '@core/requisitions-types';
import type { Requisition, RequisitionsParams } from '@core/requisitions-types';
import type {
  DataTableColumnGeometryChange,
  DataTableRow,
  IntersectionObserverTemplateContext,
} from '@core/user-interface-legacy';
import {
  DataTableComponent,
  IntersectionObserverEntryComponent,
  PlaceloaderComponent,
} from '@core/user-interface-legacy';
import { LifeCycle } from '@core/utils-legacy';
import {
  BulkActionsSelectionMessageComponent,
  BulkActionsSelectionTracker,
} from '@recruitee/bulk-actions-legacy';

import { RequisitionsListTableComponent } from '../../../components/list/requisitions-list-table/requisitions-list-table.component';
import { RequisitionsListFacade } from '../../../facades/list/requisitions-list.facade';
import type {
  RequisitionsGroupToDisplay,
  RequisitionsTableColumn,
} from '../../../requisitions.types';
import { RequisitionsBulkActionsController } from '../../../services/requisitions-bulk-actions.controller';
import { RequisitionsListTableService } from '../../../services/requisitions-list-table.service';
import { RequisitionsListViewStateService } from '../../../services/requisitions-list-view-state.service';
import { RequisitionsSharedActionsService } from '../../../services/requisitions-shared-actions.service';
import { RequisitionsListEmptyStateContainerComponent } from '../requisitions-list-empty-state-container/requisitions-list-empty-state-container.component';

@Component({
  selector: 'rt-requisitions-list-table-view-container',
  templateUrl: './requisitions-list-table-view-container.component.html',
  styleUrls: ['./requisitions-list-table-view-container.component.less'],
  animations: [collapse],
  imports: [
    RouterModule,
    RequisitionsListEmptyStateContainerComponent,
    RequisitionsListTableComponent,
    IntersectionObserverEntryComponent,
    DataTableComponent,
    PlaceloaderComponent,
    BulkActionsSelectionMessageComponent,
    OffersPageGroupHeadingComponent,
    NgTemplateAssertType,
  ],
  providers: [provideAnalyticsViewContext(AnalyticsViewContext.Requisitions)],
})
export class RequisitionsListTableViewContainerComponent {
  public readonly IntersectionObserverTemplateContext: IntersectionObserverTemplateContext<RequisitionsGroupToDisplay>;

  @Input() public requisitionsGroups: RequisitionsGroupToDisplay[] = [];
  @Input() public isLoading: boolean;
  @Input() public params: RequisitionsParams;
  @Input() public collapsedGroups: string[];
  @Input() public showGroups: boolean;

  public placeloaderRows: DataTableRow[] = Array(5)
    .fill(0)
    .map((x, i) => ({ id: i }));
  public trackById = trackByProp<{ id: number }>({ prop: 'id' });
  public trackByIndex = trackByIndex;
  public jobsMap: JobsIdMap;
  public usersMap: UserIdMap;
  public columns: RequisitionsTableColumn[];
  public autoFit: boolean;
  public canEditRequisitions: boolean;
  public canCreateJobs: boolean;
  public showRequisitions: boolean;
  public selectedIdsByGroup: Map<string | null, number[]> = new Map();
  public allSelectedIds: Record<number, boolean> = {};

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;

  constructor(
    private store: Store,
    private router: Router,
    private requisitionsListFacade: RequisitionsListFacade,
    private requisitionsSharedActionsService: RequisitionsSharedActionsService,
    private requisitionsListTableService: RequisitionsListTableService,
    private requisitionsViewState: RequisitionsListViewStateService,
    private bulkActionsController: RequisitionsBulkActionsController,
    private bulkActionsSelectionTracker: BulkActionsSelectionTracker,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.params?.currentValue &&
      this.params[RequisitionsListParam.SortBy] &&
      this.params[RequisitionsListParam.SortOrder]
    ) {
      this.requisitionsListTableService.toggleColumnSort(
        this.params[RequisitionsListParam.SortBy],
        this.params[RequisitionsListParam.SortOrder],
      );
    }

    if (changes.showGroups || changes.requisitionsGroups) {
      this.bulkActionsSelectionTracker.items = this.requisitionsGroups.reduce(
        (acc, req) => [...acc, ...req.requisitions],
        [],
      );

      this.handleRequisitionsVisibility();
    }
  }

  public ngOnInit(): void {
    combineLatest([
      this.store.select(JobsCombinedSelectors.activeItemsAndArchivedBriefIdMap),
      this.store.select(UserSelectors.idMap),
    ])
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(([jobsMap, usersMap]) => {
        this.jobsMap = jobsMap;
        this.usersMap = usersMap;
        this.cdRef.detectChanges();
      });
    this.listenToTableConfigChanges();
    this.listenToBulkActionsSelectChanges();

    this.canEditRequisitions = this.requisitionsListFacade.canEditRequisitions;
    this.canCreateJobs = this.requisitionsListFacade.canCreateJobs;
  }

  public follow(id: number): void {
    this.requisitionsSharedActionsService.follow(id);
  }

  public unfollow(id: number): void {
    this.requisitionsSharedActionsService.unfollow(id);
  }

  public createJob(id: number): void {
    this.requisitionsSharedActionsService.createJob(id);
  }

  public duplicate(id: number): void {
    this.requisitionsSharedActionsService.duplicate(id);
  }

  public delete(requisition: Requisition): void {
    this.requisitionsSharedActionsService.delete(requisition);
  }

  public archive(requisition: Requisition): void {
    this.requisitionsSharedActionsService.archive(requisition);
  }

  public retrieve(requisition: Requisition): void {
    this.requisitionsSharedActionsService.retrieve(requisition);
  }

  public cancelRequest(id: number): void {
    this.requisitionsSharedActionsService.cancelApprovalRequest(id);
  }

  public openRequisition(id: number): void {
    this.router.navigate(['requisitions', id]);
  }

  public handleGeometryChange(geometry: DataTableColumnGeometryChange) {
    this.requisitionsListTableService.handleGeometryChange(geometry);
  }

  public changeGroupVisibility(groupName: string): void {
    this.requisitionsViewState.setGroupVisibility(groupName);
  }

  public handleSortChange(column: RequisitionsTableColumn): void {
    this.requisitionsViewState.setSorting(column);
  }

  public onRowSelect(event: { row: DataTableRow; selected: boolean }): void {
    this.bulkActionsController.selectItem(<any>event.row, event.selected);
  }

  public onGroupSelectedChange(group: RequisitionsGroupToDisplay, isSelected: boolean): void {
    this.bulkActionsController.selectItems(group.requisitions, isSelected);
  }

  public handleRequisitionsVisibility() {
    if (this.showGroups) {
      this.showRequisitions = !!this.requisitionsGroups.length;
    } else {
      const requisitions = this.requisitionsGroups[0]?.requisitions;
      this.showRequisitions = requisitions ? !!requisitions.length : true;
    }
  }

  private listenToTableConfigChanges(): void {
    this.requisitionsListTableService.tableConfig$
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(config => {
        this.columns = config.visibleColumns;
        this.autoFit = config.autoFit;
        this.cdRef.detectChanges();
      });
  }

  private listenToBulkActionsSelectChanges(): void {
    this.bulkActionsController.selectedIdsDict$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(dict => {
        this.allSelectedIds = dict;
        this.selectedIdsByGroup = new Map(
          this.requisitionsGroups.map(group => [
            group.name!,
            group.requisitions.reduce(
              (acc, req) => [...acc, ...(!!dict[req.id] ? [req.id] : [])],
              [],
            ),
          ]),
        );
        this.cdRef.detectChanges();
      });
  }
}
